import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/** Merge class names, with later Tailwind utilities beating earlier conflicting ones. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** "2 minutes ago" / "3 days ago". Null-safe because most timestamps in the API are optional. */
export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const seconds = Math.round((then - Date.now()) / 1000);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["year", 31536000], ["month", 2592000], ["week", 604800],
    ["day", 86400], ["hour", 3600], ["minute", 60],
  ];
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  for (const [unit, secondsPer] of units) {
    if (Math.abs(seconds) >= secondsPer) return rtf.format(Math.round(seconds / secondsPer), unit);
  }
  return rtf.format(Math.round(seconds), "second");
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
}

/** mm:ss for call timers. Hours only appear once there are any, so the common case stays short. */
export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

/** Two-letter monogram for the avatar fallback. */
export function initials(name: string | null | undefined, fallback = "?"): string {
  if (!name) return fallback;
  const parts = name.trim().split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

/**
 * A number the way a person reads one aloud.
 *
 * Only the shape a rep sees all day is touched: a NANP number, with or without its `+1`, becomes
 * `+1 310 555 0142` — area code, exchange, line — which is how a screen reader will voice it and
 * how a human checks it against a CRM record. Everything else (an extension, a conference name,
 * a non-NANP number, a name) comes back untouched, because grouping a number we do not recognise
 * is a guess, and a wrong guess reads worse than the raw digits.
 */
export function formatPhoneNumber(raw: string): string {
  const trimmed = raw.trim();
  const digits = trimmed.replace(/[\s().-]/g, "");
  const m = /^\+?1?(\d{3})(\d{3})(\d{4})$/.exec(digits);
  return m ? `+1 ${m[1]} ${m[2]} ${m[3]}` : trimmed;
}
